import * as Yup from 'yup';
import {
  CasDisplayField,
  CasRHFTextField,
  CasRHFMultilineTextField,
  CasReactHookFormDialog,
  CasLoaderWithBackdrop,
  CasButton,
  CasErrorDisplay,
} from 'src/compass/shared/components';
import { IGeneralObjectType } from 'src/compass/site/util';
import { getEmailSchema, getMaxStringSchema, getSelectSchema } from 'src/compass/shared/schemas';
import useAddMutation from 'src/compass/shared/hooks/reactQuery/useAddMutation';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import { RHFRequestTypeSelect } from './RHFRequestTypeSelect';
import GrowerDialog from '../GrowerDialog';

interface Props {
  ids?: number[];
  onUpdated: Function;
  onClose: VoidFunction;
}

const RequestDocSchema = Yup.object().shape({
  requestTypeId: getSelectSchema("Request Type"),
  email: getEmailSchema(),
  notes: getMaxStringSchema(),
});

export default function RequestDocDialog({ ids, onUpdated, onClose }: Props) {
  const growerDialog = useCasBoolean();

  const { data, isFetching, isError, refetch } = useRetrieve({
    url: 'Sce/Claims/SendInitial/Load',
    params: { ids },
  });

  const { mutate } = useAddMutation({
    url: 'Sce/Claims/SendInitial/CreateUploadRequest',
    onSuccess: (response) => {
      onSuccessEnqueueSnackbar(response);
      onUpdated();
      onClose();
    },
  });

  if (isFetching) return <CasLoaderWithBackdrop />;
  if (isError) throw new Error('Error retrieving data from the server.');

  const defaultValues = {
    ids,
    email: data?.claimsEmail,
  };

  const onSubmit = (payload: IGeneralObjectType) => mutate(payload);

  return (
    <CasReactHookFormDialog
      open
      onClose={onClose}
      title="Request Document Upload(s)"
      defaultValues={defaultValues}
      schema={RequestDocSchema}
      onSubmit={onSubmit}
    >
      <CasDisplayField label="" value={data?.title} />
      <CasDisplayField
        label="Relationship Owner"
        value={
          data?.relationshipOwner ?? (
            <CasErrorDisplay message="Relationship Owner not assigned yet" />
          )
        }
      />
      {data?.growerId && (
        <CasButton variant="text" onClick={growerDialog.setTrue}>
          Grower
        </CasButton>
      )}
      <RHFRequestTypeSelect label="Request Type" name="requestTypeId" params={{ area: 'SCE' }} />
      <CasRHFTextField name="email" label="Email" />
      <CasRHFMultilineTextField name="notes" label="Notes" />
      {growerDialog.value && (
        <GrowerDialog
          id={data.growerId}
          insuredName={data?.title?.[0]}
          onClose={growerDialog.setFalse}
          onUpdated={refetch}
        />
      )}
    </CasReactHookFormDialog>
  );
}
